/**
 * FsmFlow — the React Flow FSM (phase 6d).
 *
 * The FSM counterpart of EdgFlow. Fully CONTROLLED: every node + edge is DERIVED
 * from the project's uimodel FSM view (`model.uimodel.findFSM(fsm)` → `.nodes` /
 * `.links`) + the FSM metamodel, inside a mobx `observer` so the derivation
 * re-runs when any observable it touches changes. MST stays the single source of
 * truth.
 *
 * Parity targets (ported from `components/FSM/index.js` + `lib/joint`):
 *  - Nodes come from the FSM view node positions/sizes; the state supplies the
 *    type (INITIAL_STATE / STATE / FINAL_STATE) + name. Shapes in FsmNodes.tsx.
 *  - Edges come from the FSM view links; each carries the persisted vertices
 *    (self-loops always have >= 2, enforced in fsmlink.js) and labelPosition. The
 *    transition geometry uses ELLIPSE boundary anchoring + the same rounded
 *    connector + the MANY arrowhead the old FSM reused for the transition end.
 *  - The transition label is the `\n`-joined method names at the persisted
 *    labelPosition, or the JointJS default `{ offset: 10 + 7 * N, distance: 0.5 }`.
 *  - The single orange highlight (`joint-highlighted`) is applied to the SELECTED
 *    state/transition (the FSM `ui.highlighted` is derived from the root store's
 *    `selected`, so selection IS the highlight — there is no separate list).
 *
 * Interactions (6d step 2, gated by `interactive`):
 *  - selection (node/edge click, no tool armed) routes through `onSelect`,
 *  - blank-canvas click deselects and, with an add-state tool armed, fires
 *    `onAddState` at the click point (the old `onPaperClick`),
 *  - state drag commits the position on drag END via `onChangePosition`
 *    (`updateFSMPosition`); the store moves the self-loop vertices for free,
 *  - the add_transition tool turns nodes into full-node connect magnets; a
 *    node-to-node (or node-to-SELF) connect fires `onAddTransition`,
 *  - vertex handles on the selected transition drag/insert/remove vertices via
 *    `onChangeVertices` (`updateFSMVertices`); self-loop 2-vertex invariant is
 *    store-enforced,
 *  - the transition label drags to persist a new labelPosition via
 *    `onChangeLabelPosition` (`updateFSMLinkLabelPosition`).
 *
 * Per-element object identity is cached by id + a revision hash (the EdgFlow
 * pattern) so unchanged elements keep the same reference across observable ticks.
 *
 * Classic JSX runtime (app-wide esbuild transform) needs React in scope.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import {
  ReactFlow,
  ReactFlowProvider,
  ViewportPortal,
  Panel,
  ConnectionMode,
  ConnectionLineType,
  useReactFlow,
  type Connection,
  type NodeChange,
  type NodeMouseHandler,
  type EdgeMouseHandler,
  type OnNodeDrag,
  type OnConnectEnd,
  type IsValidConnection,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import './fsmFlow.css';

import FsmStateNode, {
  FSM_SOURCE_HANDLE_ID,
  FSM_TARGET_HANDLE_ID,
  type FsmFlowNode,
  type FsmStateType,
  type FsmHighlightClass,
} from './FsmNodes';
import {
  clientPointFromConnectEnd,
  nodeIdFromClientPoint,
  transitionFromConnectEnd,
} from './fsmConnect';
import TransitionEdge, { type TransitionFlowEdge, type FsmEdgeLabel } from './TransitionEdge';
import { type FsmVertexDragLive } from './FsmVertexHandles';
import {
  computeEllipseEdgePath,
  labelPointWithOffset,
  labelPositionFromPoint,
  type Pt,
  type Rect,
} from './edgeGeometry';
import {
  applyFsmControlledNodeChanges,
  flowNodeRects,
  reuseUnchangedList,
} from './flowNodeState';
import {
  commitVertexInsert,
  planVertexInsert,
  removeVertex,
  type VertexSelection,
  vertexHintText,
} from './vertexEditing';
import {
  ZOOM_ON_DOUBLE_CLICK,
  consumeDiagramDoubleClick,
  isDoubleClickFollowUp,
  recoverVertexInsertFromPaneClick,
  rememberEdgeClick,
  resolveDoubleClickInsert,
  type PendingEdgeClick,
} from './diagramDoubleClick';
import { MerodeMarkerDefs } from './markers';
import { MANY, MARKER_VARIANTS } from './markerIds';
import EdgZoomToolbar, { MIN_ZOOM, MAX_ZOOM } from './EdgZoomToolbar';

// React Flow requires stable, module-level type maps.
const nodeTypes = { fsmState: FsmStateNode } as const;
const edgeTypes = { transition: TransitionEdge } as const;

// FSM state stroke widths (FsmNodes.tsx): the initial circle keeps 2, the State /
// FinalState ellipses use 1 — fed into the ellipse boundary offset.
const STATE_STROKE = 1;
const INITIAL_STROKE = 2;

// The transition end marker — the SAME MANY arrowhead the EDG uses, reused by the
// old FSM (transition.js: MARKER_TRANSITION_END = MANY). Highlighted swaps to HL.
const MARKER_TRANSITION_END = MANY;
const MARKER_TRANSITION_END_HIGHLIGHTED = MARKER_VARIANTS.HL.MANY;
const SELF_LOOP_MIN_VERTICES = 2;
const FSM_EDGE_Z_INDEX = 0;
const SELECTED_FSM_EDGE_Z_INDEX = 1001;

export type FsmFlowProps = {
  /** The FSM view store (`model.uimodel.findFSM(fsm)`). */
  // The legacy MST stores are untyped .js; `any` is the honest boundary type.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fsmView: any;
  /** Ids of highlighted states/transitions (the FSM `ui.highlighted`). */
  highlighted?: string[];
  /** Class for the floating zoom toolbar (mirrors the EDG's). */
  floatingToolbarClassName?: string;
  /**
   * EXPORT mode (phase 6e). Renders 1:1 at zoom 1 with no fitView / no
   * interactions / no floating UI so the off-screen PNG exporter measures the true
   * content bbox and crops. Matches the old Rappid `toPNG` scale-1 rasterise.
   */
  exportMode?: boolean;

  // --- Interactions (6d step 2). All OPTIONAL: when omitted the flow stays
  // render-only (the 6d step 1 behaviour). DevFsm / FsmPanel wire these to the
  // existing store flow + uimodel commands. ---

  /** Enable selection / drag / connect / vertex + label editing. */
  interactive?: boolean;
  /** The currently armed FSM tool (`ui.activeTool`), or null. */
  activeTool?: string | null;
  /** Select a state/transition by id (routes through `ui.select`/confirmable). */
  onSelect?: (id: string) => void;
  /** Blank-canvas click — deselect (always) + the add-state branch fires first. */
  onPaneClick?: () => void;
  /** Start the add-state flow at a canvas point with the given state type. */
  onAddState?: (type: FsmStateType, position: { x: number; y: number }) => void;
  /** Start the add-transition flow from a connect-drag (self-loop allowed). */
  onAddTransition?: (sourceStateId: string, targetStateId: string) => void;
  /** Commit a state's final position on drag end (existing `updateFSMPosition`). */
  onChangePosition?: (stateId: string, x: number, y: number) => void;
  /** Replace a transition's vertices (existing `updateFSMVertices`). */
  onChangeVertices?: (transitionId: string, vertices: Pt[]) => void;
  /**
   * Commit a transition label position from a dragged point (existing
   * `updateFSMLinkLabelPosition`). FsmFlow converts the point to { distance,
   * offset } against the live path before committing.
   */
  onChangeLabelPosition?: (transitionId: string, position: { offset: number; distance: number }) => void;
};

/** Resolve the FSM stroke width for a state type (initial circle = 2, else 1). */
function strokeForType(type: FsmStateType): number {
  return type === 'INITIAL_STATE' ? INITIAL_STROKE : STATE_STROKE;
}

/**
 * The method-name list for a transition label, ported from
 * `lib/joint/fsm.js:getTransitionProperties`:
 *   uniq(orderBy(metaMethods, ['provenance','name'], ['desc','asc']).map(name)).
 * Reimplemented WITHOUT lodash: sort by provenance DESC then name ASC, map to
 * names, de-duplicate preserving first occurrence.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function transitionMethodNames(transition: any): string[] {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sorted = [...transition.metaMethods].sort((a: any, b: any) => {
    if (a.provenance !== b.provenance) {
      return a.provenance < b.provenance ? 1 : -1; // descending provenance
    }
    if (a.name === b.name) return 0;
    return a.name < b.name ? -1 : 1; // ascending name
  });
  const seen = new Set<string>();
  const out: string[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  for (const m of sorted as any[]) {
    if (!seen.has(m.name)) {
      seen.add(m.name);
      out.push(m.name);
    }
  }
  return out;
}

type NodeCache = Map<string, { rev: string; node: FsmFlowNode }>;
type EdgeCache = Map<string, { rev: string; edge: TransitionFlowEdge }>;
type FsmNodeView = {
  position: Pt;
  size: { width: number; height: number };
};
type FsmViewWithNodes = {
  findNodeById: (stateId: string) => FsmNodeView | null;
};

function isTextEditingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tagName = target.tagName.toLowerCase();
  return (
    tagName === 'input' ||
    tagName === 'textarea' ||
    tagName === 'select' ||
    target.isContentEditable
  );
}

/** A node's rect from the FSM view (reads the persisted node). */
function stateRect(
  fsmView: FsmViewWithNodes,
  stateId: string,
  currentNodeRects?: Map<string, Rect>
): Rect | null {
  const currentRect = currentNodeRects?.get(stateId);
  if (currentRect) return currentRect;

  const node = fsmView.findNodeById(stateId);
  if (!node) return null;
  return {
    x: node.position.x,
    y: node.position.y,
    width: node.size.width,
    height: node.size.height,
  };
}

type NodeInteraction = {
  interactive: boolean;
  selectedId: string | null;
  /** add_transition tool armed → full-node connect magnets. */
  connectArmed: boolean;
  /** No tool armed → state drag enabled. */
  dragEnabled: boolean;
};

/**
 * Derive the React Flow nodes from the FSM view + metamodel. Called in the
 * observer's render body (NOT memoised) so every observable read re-runs the
 * derivation. Per-node identity stays stable through the revision cache.
 */
function deriveNodes(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fsmView: any,
  highlighted: string[],
  cache: NodeCache,
  interaction: NodeInteraction
): FsmFlowNode[] {
  if (!fsmView || !fsmView.fsm) return [];
  const { interactive, selectedId, connectArmed, dragEnabled } = interaction;
  const nextCache: NodeCache = new Map();
  const result: FsmFlowNode[] = [];

  for (const state of fsmView.fsm.states) {
    const id: string = state.id;
    const rect = stateRect(fsmView, id);
    if (!rect) continue; // no persisted node for this state -> not drawn
    const stateType = state.type as FsmStateType;
    const name: string = state.name;
    const highlight: FsmHighlightClass = highlighted.includes(id) ? 'joint-highlighted' : null;
    const isSelected = interactive && selectedId === id;

    const rev = [
      stateType,
      name,
      rect.x,
      rect.y,
      rect.width,
      rect.height,
      highlight ?? '',
      interactive ? '1' : '0',
      dragEnabled ? '1' : '0',
      connectArmed ? '1' : '0',
      isSelected ? '1' : '0',
    ].join('|');

    const cached = cache.get(id);
    let node: FsmFlowNode;
    if (cached && cached.rev === rev) {
      node = cached.node;
    } else {
      node = {
        id,
        type: 'fsmState',
        position: { x: rect.x, y: rect.y },
        width: rect.width,
        height: rect.height,
        // Drag only with NO tool armed (the old FSM moved a state only then).
        draggable: interactive && dragEnabled,
        selectable: interactive,
        connectable: connectArmed,
        selected: isSelected,
        data: {
          stateType,
          name,
          width: rect.width,
          height: rect.height,
          highlight,
          connectable: connectArmed,
        },
      };
    }
    nextCache.set(id, { rev, node });
    result.push(node);
  }

  cache.clear();
  for (const [k, v] of nextCache) cache.set(k, v);
  return result;
}

function fsmFlowNodeSignature(nodes: FsmFlowNode[]): string {
  return nodes
    .map((node) =>
      JSON.stringify([
        node.id,
        node.position.x,
        node.position.y,
        node.width,
        node.height,
        node.draggable,
        node.selectable,
        node.connectable,
        node.selected,
        node.data.stateType,
        node.data.name,
        node.data.highlight ?? '',
        node.data.connectable,
      ])
    )
    .join('|');
}

/**
 * Build the transition label spec: the `\n`-joined method names at the
 * labelPosition (or the JointJS default when null). The default offset is
 * `10 + 7 * methodNames.length` at distance 0.5 — verbatim from
 * `lib/joint/fsm.js:setTransitionLabel`.
 */
function buildLabel(
  id: string,
  methodNames: string[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  labelPosition: any,
  points: Pt[]
): FsmEdgeLabel | null {
  if (methodNames.length === 0) return null;
  const offset =
    labelPosition != null && labelPosition.offset != null
      ? labelPosition.offset
      : 10 + 7 * methodNames.length;
  const distance =
    labelPosition != null && labelPosition.distance != null ? labelPosition.distance : 0.5;
  const point = labelPointWithOffset(points, distance, offset);
  return { key: id, lines: methodNames, point };
}

type EdgeInteraction = {
  interactive: boolean;
  selectedId: string | null;
  selectedVertex: VertexSelection | null;
  /** Current React Flow state rects while dragging/resizing; FSM view rects are fallback. */
  currentNodeRects: Map<string, Rect>;
  /** Live vertex override per transition id during a handle drag. */
  liveVertices: Map<string, Pt[]>;
  onChangeVertices?: (transitionId: string, vertices: Pt[]) => void;
  onVertexDragLive: FsmVertexDragLive;
  onSelectVertex: (transitionId: string, index: number) => void;
  onDeleteVertex: (transitionId: string, index: number) => void;
  onChangeLabelPosition?: (transitionId: string, point: Pt) => void;
};

/**
 * Derive the React Flow edges from the FSM view links + metamodel transitions,
 * mirroring `FSMGraph`. Called in the observer body (NOT memoised). Per-edge
 * identity stays stable through the revision cache.
 */
function deriveEdges(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fsmView: any,
  highlighted: string[],
  cache: EdgeCache,
  interaction: EdgeInteraction
): TransitionFlowEdge[] {
  if (!fsmView || !fsmView.fsm) return [];
  const {
    interactive,
    selectedId,
    selectedVertex,
    currentNodeRects,
    liveVertices,
    onChangeVertices,
    onVertexDragLive,
    onSelectVertex,
    onDeleteVertex,
    onChangeLabelPosition,
  } = interaction;
  const nextCache: EdgeCache = new Map();
  const result: TransitionFlowEdge[] = [];

  for (const transition of fsmView.fsm.transitions) {
    const id: string = transition.id;
    const link = fsmView.findLinkById(id);
    if (!link) continue;

    const sourceStateId: string = transition.sourceState.id;
    const targetStateId: string = transition.targetState.id;
    const sourceRect = stateRect(fsmView, sourceStateId, currentNodeRects);
    const targetRect = stateRect(fsmView, targetStateId, currentNodeRects);
    if (!sourceRect || !targetRect) continue;

    const persistedVertices: Pt[] = link.jsVertices.map((v: Pt) => ({ x: v.x, y: v.y }));
    const override = liveVertices.get(id);
    const vertices: Pt[] = override ?? persistedVertices;
    const selfLoop = sourceStateId === targetStateId;
    const methodNames = transitionMethodNames(transition);
    const highlight: FsmHighlightClass = highlighted.includes(id) ? 'joint-highlighted' : null;
    const markerEndId = highlight
      ? MARKER_TRANSITION_END_HIGHLIGHTED
      : MARKER_TRANSITION_END;

    const sourceType = transition.sourceState.type as FsmStateType;
    const targetType = transition.targetState.type as FsmStateType;

    const path = computeEllipseEdgePath({
      sourceRect,
      targetRect,
      vertices,
      sourceStroke: strokeForType(sourceType),
      targetStroke: strokeForType(targetType),
    });

    const labelPosition = link.labelPosition;
    const label = buildLabel(id, methodNames, labelPosition, path.points);

    const isSelected = interactive && selectedId === id;
    const selectedVertexIndex =
      isSelected && selectedVertex?.edgeId === id ? selectedVertex.index : null;
    const rev = [
      'tr',
      path.d,
      markerEndId,
      highlight ?? '',
      methodNames.join('\n'),
      label ? `${label.point.x},${label.point.y}` : '',
      interactive ? '1' : '0',
      isSelected ? '1' : '0',
      selectedVertexIndex ?? '',
      // The handle positions depend on the vertices; encode so the edge re-derives
      // when the persisted (or live) vertices change while selected.
      isSelected ? JSON.stringify(vertices) : '',
    ].join('|');

    const cached = cache.get(id);
    let edge: TransitionFlowEdge;
    if (cached && cached.rev === rev) {
      edge = cached.edge;
    } else {
      edge = {
        id,
        type: 'transition',
        source: sourceStateId,
        target: targetStateId,
        sourceHandle: FSM_SOURCE_HANDLE_ID,
        targetHandle: FSM_TARGET_HANDLE_ID,
        selectable: interactive,
        selected: isSelected,
        zIndex: isSelected ? SELECTED_FSM_EDGE_Z_INDEX : FSM_EDGE_Z_INDEX,
        data: {
          d: path.d,
          markerEndId,
          highlight,
          label,
          points: path.points,
          selfLoop,
          vertices: interactive ? vertices : undefined,
          onChangeVertices: interactive ? onChangeVertices : undefined,
          onVertexDragLive: interactive ? onVertexDragLive : undefined,
          selectedVertexIndex,
          onSelectVertex: interactive ? onSelectVertex : undefined,
          onDeleteVertex: interactive ? onDeleteVertex : undefined,
          onChangeLabelPosition: interactive ? onChangeLabelPosition : undefined,
        },
      };
    }
    nextCache.set(id, { rev, edge });
    result.push(edge);
  }

  cache.clear();
  for (const [k, v] of nextCache) cache.set(k, v);
  return result;
}

/**
 * Inner flow. Lives inside a <ReactFlowProvider> so the zoom toolbar's hooks
 * resolve against this flow's instance.
 */
const FsmFlowInner = observer(function FsmFlowInner({
  fsmView,
  highlighted,
  floatingToolbarClassName = 'shifted',
  exportMode = false,
  interactive = false,
  activeTool = null,
  onSelect,
  onPaneClick,
  onAddState,
  onAddTransition,
  onChangePosition,
  onChangeVertices,
  onChangeLabelPosition,
}: FsmFlowProps) {
  const nodeCacheRef = useRef<NodeCache>(new Map());
  const edgeCacheRef = useRef<EdgeCache>(new Map());
  const prevEdgesRef = useRef<TransitionFlowEdge[]>([]);

  const { screenToFlowPosition } = useReactFlow();

  // Transient live-vertex overrides during a handle drag, keyed by transition id.
  const liveVerticesRef = useRef<Map<string, Pt[]>>(new Map());
  const [, forceVertexRender] = useState(0);
  const onVertexDragLive = useCallback<FsmVertexDragLive>((transitionId, vertices) => {
    if (vertices === null) liveVerticesRef.current.delete(transitionId);
    else liveVerticesRef.current.set(transitionId, vertices);
    forceVertexRender((n) => n + 1);
  }, []);
  const [selectedVertex, setSelectedVertex] = useState<VertexSelection | null>(null);

  const handleSelectVertex = useCallback((transitionId: string, index: number) => {
    setSelectedVertex({ edgeId: transitionId, index });
  }, []);

  const handleDeleteVertex = useCallback(
    (transitionId: string, index: number) => {
      if (!onChangeVertices || !fsmView) return;
      const link = fsmView.findLinkById(transitionId);
      if (!link) return;
      const existing: Pt[] = link.jsVertices.map((v: Pt) => ({ x: v.x, y: v.y }));
      if (index < 0 || index >= existing.length) return;

      const transition = fsmView.fsm.transitions.find(
        (item: { id: string }) => item.id === transitionId
      );
      const selfLoop =
        transition != null && transition.sourceState.id === transition.targetState.id;
      if (selfLoop && existing.length <= SELF_LOOP_MIN_VERTICES) return;

      liveVerticesRef.current.delete(transitionId);
      forceVertexRender((n) => n + 1);
      onChangeVertices(
        transitionId,
        removeVertex(existing, index, {
          minVertices: selfLoop ? SELF_LOOP_MIN_VERTICES : undefined,
        })
      );
      setSelectedVertex(null);
    },
    [fsmView, onChangeVertices]
  );

  const selectedId: string | null = interactive ? resolveSelectedId(highlighted) : null;

  // The add-state tools (pane click adds a state); the add-transition tool (connect).
  const addStateType = stateTypeForTool(activeTool);
  const connectArmed = interactive && activeTool === 'add_transition';
  const dragEnabled = interactive && activeTool == null;

  useEffect(() => {
    if (selectedVertex && selectedVertex.edgeId !== selectedId) setSelectedVertex(null);
  }, [selectedId, selectedVertex]);

  useEffect(() => {
    if (!interactive || !selectedVertex || !onChangeVertices) return;

    const handleKeyDown = (evt: KeyboardEvent) => {
      if (isTextEditingTarget(evt.target)) return;
      if (evt.key === 'Delete' || evt.key === 'Backspace') {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        handleDeleteVertex(selectedVertex.edgeId, selectedVertex.index);
      } else if (evt.key === 'Escape') {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        setSelectedVertex(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [handleDeleteVertex, interactive, onChangeVertices, selectedVertex]);

  // Bend-point hover hint — same as EDG's `linkHover`.
  // Hovering a bend handle (part of the edge group) swaps in the remove hint.
  const [edgeHover, setEdgeHover] = useState(false);
  const [vertexHover, setVertexHover] = useState(false);
  const handleEdgeMouseEnter = useCallback(() => setEdgeHover(true), []);
  const handleEdgeMouseMove = useCallback((evt: React.MouseEvent) => {
    const target = evt.target instanceof Element ? evt.target : null;
    setVertexHover(target?.closest('.merode-vertex-handle') != null);
  }, []);
  const handleEdgeMouseLeave = useCallback(() => {
    setEdgeHover(false);
    setVertexHover(false);
  }, []);
  const vertexHint = vertexHintText({
    edgeHover,
    vertexHover,
    vertexSelected: selectedVertex != null,
  });

  // Read the highlight list reactively (so the observer tracks it).
  const highlightedIds: string[] = highlighted ? [...highlighted] : [];

  // --- Label position commit: convert the dragged FLOW point to { distance,
  // offset } against the transition's current path, then commit. ---
  const handleChangeLabelPosition = useCallback(
    (transitionId: string, point: Pt) => {
      if (!onChangeLabelPosition || !fsmView || !fsmView.fsm) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const transition = fsmView.fsm.transitions.find((t: any) => t.id === transitionId);
      if (!transition) return;
      const link = fsmView.findLinkById(transitionId);
      if (!link) return;
      const sourceRect = stateRect(fsmView, transition.sourceState.id);
      const targetRect = stateRect(fsmView, transition.targetState.id);
      if (!sourceRect || !targetRect) return;
      const path = computeEllipseEdgePath({
        sourceRect,
        targetRect,
        vertices: link.jsVertices.map((v: Pt) => ({ x: v.x, y: v.y })),
        sourceStroke: strokeForType(transition.sourceState.type as FsmStateType),
        targetStroke: strokeForType(transition.targetState.type as FsmStateType),
      });
      const position = labelPositionFromPoint(path.points, point);
      onChangeLabelPosition(transitionId, position);
    },
    [onChangeLabelPosition, fsmView]
  );

  const baseNodes = deriveNodes(fsmView, highlightedIds, nodeCacheRef.current, {
    interactive,
    selectedId,
    connectArmed,
    dragEnabled,
  });
  const baseNodesSignature = fsmFlowNodeSignature(baseNodes);
  const latestBaseNodesRef = useRef<FsmFlowNode[]>(baseNodes);
  latestBaseNodesRef.current = baseNodes;
  const dragActiveRef = useRef(false);
  const [flowNodes, setFlowNodes] = useState<FsmFlowNode[]>(() => baseNodes);

  useEffect(() => {
    if (!dragActiveRef.current) setFlowNodes(latestBaseNodesRef.current);
  }, [baseNodesSignature]);

  const nodes = baseNodes.length === 0 ? baseNodes : flowNodes.length > 0 ? flowNodes : baseNodes;
  const currentNodeRects = flowNodeRects(nodes);
  const derivedEdges = deriveEdges(fsmView, highlightedIds, edgeCacheRef.current, {
    interactive,
    selectedId,
    selectedVertex,
    currentNodeRects,
    liveVertices: liveVerticesRef.current,
    onChangeVertices,
    onVertexDragLive,
    onSelectVertex: handleSelectVertex,
    onDeleteVertex: handleDeleteVertex,
    onChangeLabelPosition: interactive ? handleChangeLabelPosition : undefined,
  });
  const edges = reuseUnchangedList(prevEdgesRef.current, derivedEdges);
  prevEdgesRef.current = edges;
  const edgesRef = useRef(edges);
  edgesRef.current = edges;
  const pendingEdgeClickRef = useRef<PendingEdgeClick | null>(null);
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;

  const eventFlowPoint = useCallback(
    (evt: React.MouseEvent): Pt => {
      const flow = screenToFlowPosition({ x: evt.clientX, y: evt.clientY });
      return { x: Math.round(flow.x), y: Math.round(flow.y) };
    },
    [screenToFlowPosition]
  );

  // --- Vertex add: dblclick OR shift+click on a transition --------------------
  // Takes a FLOW point, not the event: a double-click inserts at its first
  // click, captured before the inspector reflowed the panes.
  const insertVertex = useCallback(
    (edgeId: string, point: Pt) => {
      if (!onChangeVertices || !fsmView) return;
      const edge = edgesRef.current.find((item) => item.id === edgeId);
      if (!edge) return;
      const edgeData = edge.data as { points?: Pt[] } | undefined;
      const link = fsmView.findLinkById(edgeId);
      const existing: Pt[] = link ? link.jsVertices.map((v: Pt) => ({ x: v.x, y: v.y })) : [];
      const plan = planVertexInsert({
        edgeId,
        existing,
        points: edgeData?.points ?? [],
        point,
      });
      commitVertexInsert(plan, {
        persistVertices: (id, vertices) => onChangeVertices(id, vertices),
        // ui.select toggles: re-selecting the selected transition would
        // deselect it and close the inspector.
        selectEdge: (id) => {
          if (onSelect && selectedIdRef.current !== id) onSelect(id);
        },
      });
      setSelectedVertex(null);
    },
    [onChangeVertices, onSelect, fsmView]
  );

  // --- Selection (node/edge click, no tool armed) ----------------------------
  const handleNodeClick: NodeMouseHandler = useCallback(
    (evt, node) => {
      if (!interactive) return;
      // The reflow slid a state under the second click of a transition
      // double-click.
      const recovered = recoverVertexInsertFromPaneClick({
        pending: pendingEdgeClickRef.current,
        detail: evt.detail,
      });
      pendingEdgeClickRef.current = null;
      if (recovered && onChangeVertices && activeTool == null) {
        insertVertex(recovered.edgeId, recovered.point);
        return;
      }
      setSelectedVertex(null);
      if (activeTool == null && onSelect) onSelect(node.id);
    },
    [interactive, activeTool, onSelect, onChangeVertices, insertVertex]
  );

  const handleEdgeClick: EdgeMouseHandler = useCallback(
    (evt, edge) => {
      if (!interactive) return;
      // Second click of a double-click: leave the selection alone (toggling it
      // off would close the inspector and reflow the panes again); the
      // dblclick handler inserts the knikpunt.
      if (isDoubleClickFollowUp({ pending: pendingEdgeClickRef.current, detail: evt.detail })) {
        return;
      }
      const point = eventFlowPoint(evt);
      pendingEdgeClickRef.current = rememberEdgeClick(edge.id, point);
      // Shift+click inserts a vertex. The insert selects the transition so the
      // new knikpunt handle is visible even if it was not selected.
      if (evt.shiftKey) {
        insertVertex(edge.id, point);
        return;
      }
      setSelectedVertex(null);
      if (activeTool == null && onSelect) onSelect(edge.id);
    },
    [interactive, activeTool, onSelect, insertVertex, eventFlowPoint]
  );

  const handleEdgeDoubleClick: EdgeMouseHandler = useCallback(
    (evt, edge) => {
      if (!interactive || !onChangeVertices) return;
      consumeDiagramDoubleClick(evt);
      // Shift+click already inserted on the first click of this double-click.
      if (evt.shiftKey) {
        pendingEdgeClickRef.current = null;
        return;
      }
      const target = resolveDoubleClickInsert({
        pending: pendingEdgeClickRef.current,
        edgeId: edge.id,
        point: eventFlowPoint(evt),
      });
      pendingEdgeClickRef.current = null;
      insertVertex(target.edgeId, target.point);
    },
    [interactive, onChangeVertices, insertVertex, eventFlowPoint]
  );

  // --- Blank-canvas click: add a state with the armed tool, then deselect ----
  const handlePaneClick = useCallback(
    (evt: React.MouseEvent) => {
      if (!interactive) return;
      // First click of a double-click on a transition opens the inspector and
      // can shrink this pane (EDG/OET sharing the row). The second click then
      // misses the hit path; recover the knikpunt insert instead of zooming /
      // re-selecting the FSM root.
      const recovered = recoverVertexInsertFromPaneClick({
        pending: pendingEdgeClickRef.current,
        detail: evt.detail,
      });
      pendingEdgeClickRef.current = null;
      if (recovered && onChangeVertices && activeTool == null) {
        consumeDiagramDoubleClick(evt);
        insertVertex(recovered.edgeId, recovered.point);
        return;
      }
      if (addStateType && onAddState) {
        const flow = screenToFlowPosition({ x: evt.clientX, y: evt.clientY });
        onAddState(addStateType, { x: Math.round(flow.x), y: Math.round(flow.y) });
      }
      setSelectedVertex(null);
      if (onPaneClick) onPaneClick();
    },
    [
      interactive,
      activeTool,
      addStateType,
      onAddState,
      onPaneClick,
      onChangeVertices,
      insertVertex,
      screenToFlowPosition,
    ]
  );

  // --- Connect (add_transition) — self-loop allowed (drag onto same node) ----
  const handleConnect = useCallback(
    (connection: Connection) => {
      if (!interactive || activeTool !== 'add_transition' || !onAddTransition) return;
      const sourceId = connection.source;
      const targetId = connection.target;
      if (sourceId == null || targetId == null) return;
      onAddTransition(sourceId, targetId);
    },
    [interactive, activeTool, onAddTransition]
  );

  // React Flow Loose mode still rejects a drop on the same node when the
  // pointer hits the handle the drag started from (full-node magnets overlap).
  // onConnect never fires in that case; recover the self-loop from connect-end.
  const handleConnectEnd: OnConnectEnd = useCallback(
    (event, connectionState) => {
      if (!interactive || activeTool !== 'add_transition' || !onAddTransition) return;
      const point = clientPointFromConnectEnd(event);
      const droppedNodeId = point ? nodeIdFromClientPoint(point.x, point.y) : null;
      const selfLoop = transitionFromConnectEnd({
        fromNodeId: connectionState.fromNode?.id,
        droppedNodeId,
        connectionIsValid: connectionState.isValid,
      });
      if (selfLoop) onAddTransition(selfLoop.source, selfLoop.target);
    },
    [interactive, activeTool, onAddTransition]
  );

  const isFsmConnectionValid = useCallback<IsValidConnection<TransitionFlowEdge>>(
    (connection) => connection.source != null && connection.target != null,
    []
  );

  // --- Drag live via React Flow state → commit position on drag END ----------
  const handleNodeDragStart: OnNodeDrag = useCallback(() => {
    dragActiveRef.current = true;
  }, []);

  const handleNodeDragStop: OnNodeDrag = useCallback(
    (_evt, node, draggedNodes) => {
      if (!interactive) return;
      const movingNodes = draggedNodes.length > 0 ? draggedNodes : [node];
      if (onChangePosition) {
        for (const movingNode of movingNodes) {
          onChangePosition(
            movingNode.id,
            Math.round(movingNode.position.x),
            Math.round(movingNode.position.y)
          );
        }
      }
      dragActiveRef.current = false;
    },
    [interactive, onChangePosition]
  );

  // React Flow controlled-node contract: apply its transient position changes
  // locally, then persist model positions only on drag stop. Dimension reports
  // are ignored — FSM states are not resizable, and feeding measurements back
  // retriggers setEdges (React #185 on create-transitions from the initial dot).
  const handleNodesChange = useCallback((changes: NodeChange<FsmFlowNode>[]) => {
    setFlowNodes((current) =>
      applyFsmControlledNodeChanges(
        changes,
        current.length > 0 ? current : latestBaseNodesRef.current
      )
    );
  }, []);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      minZoom={MIN_ZOOM}
      maxZoom={MAX_ZOOM}
      // ConnectionMode.Loose lets the single source+target handle pair accept a
      // drag in both directions (incl. back onto the same node — a self-loop),
      // matching the Rappid full-node magnet.
      connectionMode={ConnectionMode.Loose}
      connectionLineType={ConnectionLineType.Straight}
      nodesDraggable={interactive && dragEnabled}
      nodesConnectable={connectArmed}
      elementsSelectable={interactive}
      selectNodesOnDrag={false}
      onNodesChange={interactive ? handleNodesChange : undefined}
      onNodeClick={interactive ? handleNodeClick : undefined}
      onNodeDragStart={interactive ? handleNodeDragStart : undefined}
      onNodeDragStop={interactive ? handleNodeDragStop : undefined}
      onConnect={interactive ? handleConnect : undefined}
      onConnectEnd={interactive ? handleConnectEnd : undefined}
      isValidConnection={connectArmed ? isFsmConnectionValid : undefined}
      onEdgeClick={interactive ? handleEdgeClick : undefined}
      onEdgeDoubleClick={interactive ? handleEdgeDoubleClick : undefined}
      onEdgeMouseEnter={interactive ? handleEdgeMouseEnter : undefined}
      onEdgeMouseMove={interactive ? handleEdgeMouseMove : undefined}
      onEdgeMouseLeave={interactive ? handleEdgeMouseLeave : undefined}
      onPaneClick={interactive ? handlePaneClick : undefined}
      // Pan on blank-area drag + wheel zoom, BUT not while connecting (a
      // node-to-node drag must create a transition, not pan). Double-click
      // on a transition adds a knikpunt; do not also zoom the paper.
      panOnDrag={!connectArmed}
      zoomOnScroll={!exportMode}
      zoomOnPinch={!exportMode}
      zoomOnDoubleClick={ZOOM_ON_DOUBLE_CLICK}
      panOnScroll={false}
      preventScrolling={!exportMode}
      proOptions={{ hideAttribution: true }}
      // EXPORT mode: 1:1 at zoom 1, no fitView (see EdgFlow / exportImage).
      fitView={!exportMode}
      fitViewOptions={{ padding: 0.2, minZoom: MIN_ZOOM, maxZoom: 1 }}
      defaultViewport={exportMode ? { x: 0, y: 0, zoom: 1 } : undefined}
    >
      {/*
        Markers MUST live inside the React Flow VIEWPORT subtree (the element the
        PNG export clones), so inject the <defs> through <ViewportPortal>.
      */}
      <ViewportPortal>
        <svg width={0} height={0} style={{ position: 'absolute' }} aria-hidden>
          <MerodeMarkerDefs />
        </svg>
      </ViewportPortal>
      {/*
        The floating zoom cluster renders through React Flow's <Panel> so it sits
        ABOVE the pane and stays clickable (same stacking reasoning as EdgFlow).
      */}
      {!exportMode ? (
        <Panel
          position="bottom-right"
          className={`edg-zoom-panel${
            floatingToolbarClassName ? ` ${floatingToolbarClassName}` : ''
          }`}
        >
          <EdgZoomToolbar className={floatingToolbarClassName} name="FSM" />
        </Panel>
      ) : null}
      {/*
        Vertex hint, shown while hovering any transition — port of the old FSM
        Paper wrapper's `.link-help` div (components/FSM/styles.scss).
      */}
      {interactive && vertexHint ? (
        <Panel position="bottom-left" className="edg-link-help-panel">
          <div className="link-help">{vertexHint}</div>
        </Panel>
      ) : null}
    </ReactFlow>
  );
});

/**
 * Map an FSM add-state tool to the state type it creates. The four FSM tools are
 * `add_initial_state` / `add_state` / `add_final_state` / `add_transition`
 * (stores/ui/fsm.js); only the first three create a state on a pane click.
 */
function stateTypeForTool(activeTool: string | null): FsmStateType | null {
  if (activeTool === 'add_initial_state') return 'INITIAL_STATE';
  if (activeTool === 'add_state') return 'STATE';
  if (activeTool === 'add_final_state') return 'FINAL_STATE';
  return null;
}

/**
 * The FSM `ui.highlighted` is a single-element list derived from the root store's
 * `selected` (stores/ui/fsm.js), so the selected id is just that list's only
 * member. Resolve it for the React Flow `selected` flag.
 */
function resolveSelectedId(highlighted: string[] | undefined): string | null {
  if (highlighted && highlighted.length > 0) return highlighted[0];
  return null;
}

/**
 * Public FsmFlow: wraps the inner flow in a ReactFlowProvider so the toolbar can
 * use React Flow hooks.
 */
export default function FsmFlow(props: FsmFlowProps) {
  return (
    <ReactFlowProvider>
      <FsmFlowInner {...props} />
    </ReactFlowProvider>
  );
}
